/**
 * illstinct Cart Drawer (ersetzt Opus)
 * ------------------------------------------------------------------
 * Markup + alle Preise/Summen: sections/mini-cart.liquid (serverseitig, Liquid)
 * Rendering nach Änderungen: Focal CartDrawer (Event "cart:refresh" + Section Rendering API)
 *
 * Diese Datei kümmert sich nur um:
 *  - Mengenänderungen / Entfernen (entprellt, strikt nacheinander, per Line-Item-Key)
 *  - Aktionscodes einlösen / entfernen (/cart/update.js, Parameter "discount")
 *  - Lieferdatum (Logik aus dem bisherigen Opus-Code: Sonntage werden übersprungen)
 *  - Animation der Reward Bar
 */
(() => {
  if (window.__illCartDrawerLoaded) return;
  window.__illCartDrawerLoaded = true;

  const root = document.documentElement;
  const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
  const QUANTITY_DEBOUNCE = 350;

  const getDrawer = () => document.getElementById('mini-cart');
  const getSectionId = () => getDrawer()?.getAttribute('section') || 'mini-cart';
  const shopRoot = () => window.Shopify?.routes?.root || '/';

  /* ------------------------------------------------------------------
   * Warenkorb-Requests: laufen strikt nacheinander, damit keine ältere
   * Antwort einen neueren Warenkorbstand überschreibt.
   * ------------------------------------------------------------------ */
  let queue = Promise.resolve();
  let queueLength = 0;

  const enqueue = (task) => {
    queueLength++;
    const run = queue.then(task).finally(() => queueLength--);
    queue = run.catch(() => {});
    return run;
  };

  async function cartRequest(endpoint, body) {
    const response = await fetch(`${shopRoot()}cart/${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ ...body, sections: [getSectionId()], sections_url: window.location.pathname })
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Der Warenkorb konnte nicht aktualisiert werden.');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function fetchCart() {
    const response = await fetch(`${shopRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return response.json();
  }

  // Gibt den neuen Warenkorb an Focal weiter: Header-Zähler, Drawer-Neurendering usw.
  function publish(cart) {
    if (cart.sections && !cart.sections[getSectionId()]) {
      delete cart.sections; // Focal lädt die Section dann selbst nach
    }
    root.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, replacementDelay: 0 } }));
  }

  // Lädt den Drawer vollständig neu vom Server (z. B. nach Fehlern oder bfcache)
  function refreshFromServer() {
    root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: {} }));
  }

  /* ------------------------------------------------------------------
   * Mengen
   * ------------------------------------------------------------------ */
  const pending = new Map(); // key -> { quantity, timer }
  const itemErrors = new Map(); // key -> Fehlermeldung

  const findItem = (key) => getDrawer()?.querySelector(`[data-ill-item][data-key="${CSS.escape(key)}"]`);

  function changeQuantity(item, direction) {
    const key = item.dataset.key;
    const step = parseInt(item.dataset.step, 10) || 1;
    const min = parseInt(item.dataset.min, 10) || 1;
    const max = item.dataset.max ? parseInt(item.dataset.max, 10) : Infinity;
    const current = pending.has(key) ? pending.get(key).quantity : parseInt(item.dataset.quantity, 10);

    let next = current + direction * step;
    if (next < min) next = 0; // Minus bei Mindestmenge entfernt den Artikel
    if (next > max) {
      itemErrors.set(key, 'Keine weiteren Artikel auf Lager.');
      paintItemStates();
      return;
    }
    if (next === current) return;

    itemErrors.delete(key);
    schedule(key, next, next === 0 ? 0 : QUANTITY_DEBOUNCE);
  }

  function schedule(key, quantity, delay) {
    const entry = pending.get(key) || {};
    clearTimeout(entry.timer);
    entry.quantity = quantity;
    entry.timer = setTimeout(() => commit(key), delay);
    pending.set(key, entry);
    paintItemStates();
  }

  function commit(key) {
    const entry = pending.get(key);
    if (!entry || !entry.timer) return queue;

    clearTimeout(entry.timer);
    entry.timer = null;
    const quantity = entry.quantity;

    return enqueue(async () => {
      try {
        const cart = await cartRequest('change.js', { id: key, quantity });
        // Nur aufräumen, wenn in der Zwischenzeit keine neuere Menge gewählt wurde
        if (pending.get(key) === entry && !entry.timer && entry.quantity === quantity) {
          pending.delete(key);
        }
        publish(cart);
      } catch (error) {
        if (pending.get(key) === entry && !entry.timer) pending.delete(key);
        itemErrors.set(key, error.message);
        refreshFromServer();
      }
    });
  }

  // Alle offenen Mengenänderungen sofort senden und auf den Abschluss warten
  function flush() {
    Array.from(pending.keys()).forEach(commit);
    return queue;
  }

  function paintItemStates() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.querySelectorAll('[data-ill-item]').forEach((item) => {
      const key = item.dataset.key;
      const entry = pending.get(key);
      const value = item.querySelector('[data-ill-qty-value]');

      if (entry && value && entry.quantity > 0) value.textContent = entry.quantity;
      item.classList.toggle('is-loading', Boolean(entry) && entry.quantity > 0);
      item.classList.toggle('is-removing', Boolean(entry) && entry.quantity === 0);

      const errorEl = item.querySelector('[data-ill-item-error]');
      if (errorEl) {
        errorEl.textContent = itemErrors.get(key) || '';
        errorEl.hidden = !itemErrors.has(key);
      }
    });
  }

  /* ------------------------------------------------------------------
   * Aktionscodes
   * ------------------------------------------------------------------ */
  let discountMessage = null; // { type: 'error' | 'success', text, value }
  let discountExpanded = false; // eingeklapptes Aktionscode-Feld: vom Kunden geöffnet?

  // Eingeklapptes Feld bleibt offen, solange der Kunde es nutzt (auch nach Neurendern)
  function paintDiscountToggle() {
    const drawer = getDrawer();
    const form = drawer?.querySelector('[data-ill-discount-form][data-ill-collapsible]');
    if (!form) return;
    const open = discountExpanded || Boolean(discountMessage);
    form.hidden = !open;
    const toggle = drawer.querySelector('[data-ill-discount-toggle]');
    if (toggle) {
      toggle.hidden = open;
      toggle.setAttribute('aria-expanded', String(open));
    }
  }
  let messageTimer = null;
  const MESSAGE_DURATION = 3000;

  const sameCode = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

  function applyDiscount(code) {
    discountMessage = null;
    setDiscountBusy(true);

    return flush()
      .then(() => enqueue(async () => {
        const current = await fetchCart();
        if (!Array.isArray(current.discount_codes)) {
          throw new Error('Aktionscodes können gerade nicht im Warenkorb eingelöst werden. Bitte gib den Code im Checkout ein.');
        }

        const active = current.discount_codes.filter((d) => d.applicable).map((d) => d.code);
        if (active.some((c) => sameCode(c, code))) {
          discountMessage = { type: 'error', text: `Der Aktionscode „${code}“ ist bereits eingelöst.` };
          return;
        }

        const cart = await cartRequest('update.js', { discount: [...active, code].join(',') });
        const codes = cart.discount_codes || [];
        const applied = codes.find((d) => sameCode(d.code, code));

        if (applied && applied.applicable) {
          // Nicht kombinierbare, ältere Codes wieder entfernen, damit nichts „hängen“ bleibt
          const dropped = codes.filter((d) => !d.applicable).map((d) => d.code);
          if (dropped.length) {
            const cleaned = await cartRequest('update.js', { discount: codes.filter((d) => d.applicable).map((d) => d.code).join(',') });
            discountMessage = { type: 'success', text: `„${applied.code}“ wurde eingelöst. ${dropped.map((c) => `„${c}“`).join(', ')} ist damit nicht kombinierbar und wurde entfernt.` };
            publish(cleaned);
          } else {
            publish(cart);
          }
          discountExpanded = false;
          return;
        }

        // Ungültig / nicht anwendbar: Code wieder entfernen, Hinweis anzeigen
        const cleaned = await cartRequest('update.js', { discount: active.join(',') });
        discountMessage = { type: 'error', text: 'Der Rabattcode kann nicht angewendet werden.', value: code };
        publish(cleaned);
      }))
      .catch((error) => {
        discountMessage = { type: 'error', text: error.message, value: code };
      })
      .finally(() => {
        setDiscountBusy(false);
        paintDiscountMessage();
      });
  }

  function removeDiscount(code) {
    discountMessage = null;

    return flush()
      .then(() => enqueue(async () => {
        const current = await fetchCart();
        const remaining = (current.discount_codes || [])
          .filter((d) => d.applicable && !sameCode(d.code, code))
          .map((d) => d.code);
        publish(await cartRequest('update.js', { discount: remaining.join(',') }));
      }))
      .catch((error) => {
        discountMessage = { type: 'error', text: error.message };
        paintDiscountMessage();
      });
  }

  function setDiscountBusy(busy) {
    const button = getDrawer()?.querySelector('[data-ill-discount-form] button[type="submit"]');
    if (button) button.setAttribute('aria-busy', String(busy));
  }

  function paintDiscountMessage() {
    const drawer = getDrawer();
    const message = drawer?.querySelector('[data-ill-discount-message]');
    if (!message) return;

    message.hidden = !discountMessage;
    message.textContent = discountMessage ? discountMessage.text : '';
    message.classList.toggle('ill-cart__notice--error', discountMessage?.type !== 'success');
    message.classList.remove('is-hiding');

    // Hinweis nur kurz einblenden und dann automatisch ausblenden
    if (discountMessage && !discountMessage.hideScheduled) {
      const current = discountMessage;
      current.hideScheduled = true;
      clearTimeout(messageTimer);
      messageTimer = setTimeout(() => {
        if (discountMessage !== current) return;
        getDrawer()?.querySelector('[data-ill-discount-message]')?.classList.add('is-hiding');
        messageTimer = setTimeout(() => {
          if (discountMessage !== current) return;
          discountMessage = null;
          paintDiscountMessage();
        }, 300);
      }, MESSAGE_DURATION);
    }

    const input = drawer.querySelector('[data-ill-discount-form] input');
    if (input && discountMessage?.value && !input.value) input.value = discountMessage.value;
  }

  /* ------------------------------------------------------------------
   * Lieferdatum (Logik aus dem bisherigen Opus-Code)
   * Ab morgen gerechnet, Sonntage werden übersprungen.
   * ------------------------------------------------------------------ */
  function addDeliveryDays(from, days) {
    const date = new Date(from);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0) added++;
    }
    return date;
  }

  const formatDate = (date) => `${WEEKDAYS[date.getDay()]} ${date.getDate()}.${date.getMonth() + 1}.`;

  function paintDelivery() {
    const delivery = getDrawer()?.querySelector('[data-ill-delivery]');
    if (!delivery || delivery.dataset.min === '' || delivery.dataset.max === '') return;

    const today = new Date();
    const start = addDeliveryDays(today, parseInt(delivery.dataset.min, 10));
    const end = addDeliveryDays(today, parseInt(delivery.dataset.max, 10));
    delivery.querySelector('[data-ill-delivery-text]').textContent = `Lieferung ca. ${formatDate(start)} - ${formatDate(end)}`;
  }

  /* ------------------------------------------------------------------
   * Reward Bar: sanft vom alten zum neuen Fortschritt animieren
   * ------------------------------------------------------------------ */
  let lastProgress = null;

  function paintProgress() {
    const fill = getDrawer()?.querySelector('[data-ill-progress]');
    if (!fill) {
      lastProgress = null;
      return;
    }

    const target = parseFloat(fill.dataset.illProgress) || 0;
    if (lastProgress !== null && lastProgress !== target) {
      fill.style.transition = 'none';
      fill.style.setProperty('--ill-progress', lastProgress);
      fill.getBoundingClientRect(); // Reflow erzwingen
      fill.style.transition = '';
      requestAnimationFrame(() => fill.style.setProperty('--ill-progress', target));
    }
    lastProgress = target;
  }

  /* ------------------------------------------------------------------
   * Nach jedem (Neu-)Rendern des Drawers
   * ------------------------------------------------------------------ */
  // Header-Zähler synchronisieren – auch wenn Apps (z. B. MBC Bundles) den Warenkorb
  // ändern und dabei nur den Drawer neu laden, ohne "cart:updated" auszulösen.
  function syncCartCount() {
    const count = getDrawer()?.querySelector('[data-ill-item-count]')?.dataset.illItemCount;
    if (count === undefined) return;
    document.querySelectorAll('cart-count').forEach((el) => {
      if (el.textContent.trim() !== count) el.textContent = count;
    });
  }

  /* Über ALLEN Elementen anzeigen (Cookie-Banner, Custom-Liquid-Banner, Pop-ups …):
     Beim Öffnen kommt der Drawer per Popover-API in den „Top Layer“ des Browsers –
     der liegt unabhängig von jedem z-index über der Seite. Ältere Browser nutzen den
     maximalen z-index aus dem CSS. */
  const supportsTopLayer = typeof HTMLElement.prototype.showPopover === 'function';
  let hidePopoverTimer = null;

  function syncOpenState() {
    const drawer = getDrawer();
    const isOpen = Boolean(drawer?.hasAttribute('open'));
    root.classList.toggle('ill-cart-open', isOpen);
    if (isOpen) setupUpsells();
    if (!drawer || !supportsTopLayer || window.Shopify?.designMode) return;

    if (!drawer.hasAttribute('popover')) drawer.setAttribute('popover', 'manual');
    clearTimeout(hidePopoverTimer);

    if (isOpen) {
      if (!drawer.matches(':popover-open')) drawer.showPopover();
    } else if (drawer.matches(':popover-open')) {
      // Kein Schließen-Animation mehr: direkt (im nächsten Tick) aus dem Top Layer nehmen
      hidePopoverTimer = setTimeout(() => {
        if (!drawer.hasAttribute('open') && drawer.matches(':popover-open')) drawer.hidePopover();
      }, 0);
    }
  }

  function onRender() {
    if (bundleSyncFinished) setBundleSyncing(false);
    syncCartCount();
    paintDelivery();
    paintProgress();
    paintItemStates();
    paintDiscountToggle();
    paintDiscountMessage();
    trackRemovedProducts();
    setupUpsells();
  }

  let observedDrawer = null;
  /* Focal ersetzt beim Neurendern den kompletten Drawer-Inhalt. Damit Banner und
     Produktbilder nicht flackern, setzen wir die bereits geladenen <img>-Elemente
     (gleiche Quelle) wieder ein, bevor der Browser neu zeichnet. */
  function reuseLoadedImages(mutations) {
    const loaded = new Map();
    mutations.forEach((m) => m.removedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      (node.matches('img') ? [node] : node.querySelectorAll('img')).forEach((img) => {
        const src = img.getAttribute('src');
        if (src && img.complete && img.naturalWidth > 0 && !loaded.has(src)) loaded.set(src, img);
      });
    }));
    if (!loaded.size) return;

    getDrawer()?.querySelectorAll('img').forEach((img) => {
      const old = loaded.get(img.getAttribute('src'));
      if (old && old !== img) {
        old.className = img.className;
        img.replaceWith(old);
        loaded.delete(img.getAttribute('src'));
      }
    });
  }

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((m) => m.type === 'attributes')) syncOpenState();
    if (mutations.some((m) => m.type === 'childList')) {
      reuseLoadedImages(mutations);
      onRender();
    }
  });

  function observeDrawer() {
    const drawer = getDrawer();
    if (!drawer || drawer === observedDrawer) return;

    observer.disconnect();
    observer.observe(drawer, { childList: true, attributes: true, attributeFilter: ['open'] });
    observedDrawer = drawer;
    syncOpenState();
    onRender();
  }

  /* ------------------------------------------------------------------
   * Events (einmalig, per Delegation – überlebt jedes Neurendern)
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const drawer = event.target.closest('#mini-cart');
    if (!drawer) return;

    const discountToggle = event.target.closest('[data-ill-discount-toggle]');
    if (discountToggle) {
      discountExpanded = true;
      paintDiscountToggle();
      drawer.querySelector('[data-ill-discount-form] input')?.focus();
      return;
    }

    const upsellNav = event.target.closest('[data-ill-upsell-nav]');
    if (upsellNav) {
      goToUpsell(upsellNav.closest('[data-ill-upsells]'), upsellIndex + parseInt(upsellNav.dataset.illUpsellNav, 10));
      return;
    }

    const upsellDot = event.target.closest('[data-ill-upsell-dot]');
    if (upsellDot) {
      goToUpsell(upsellDot.closest('[data-ill-upsells]'), parseInt(upsellDot.dataset.illUpsellDot, 10));
      return;
    }

    const upsellAdd = event.target.closest('[data-ill-upsell-add]');
    if (upsellAdd) {
      event.preventDefault();
      const slide = upsellAdd.closest('[data-ill-upsell]');
      if (upsellAdd.hasAttribute('data-ill-upsell-picker')) {
        try {
          openPicker(JSON.parse(slide.querySelector('[data-ill-upsell-json]').textContent));
        } catch (error) {
          addVariant(upsellAdd.dataset.variantId, upsellAdd).catch(() => {});
        }
      } else {
        addVariant(upsellAdd.dataset.variantId, upsellAdd).catch((error) => {
          discountMessage = { type: 'error', text: error.message };
          paintDiscountMessage();
        });
      }
      return;
    }

    const qtyButton = event.target.closest('[data-ill-qty]');
    if (qtyButton) {
      event.preventDefault();
      const item = qtyButton.closest('[data-ill-item]');
      if (item && !qtyButton.disabled) changeQuantity(item, parseInt(qtyButton.dataset.illQty, 10));
      return;
    }

    const removeButton = event.target.closest('[data-ill-remove]');
    if (removeButton) {
      event.preventDefault();
      const item = removeButton.closest('[data-ill-item]');
      if (item) {
        itemErrors.delete(item.dataset.key);
        schedule(item.dataset.key, 0, 0);
      }
      return;
    }

    const removeCode = event.target.closest('[data-ill-remove-code]');
    if (removeCode) {
      event.preventDefault();
      removeCode.disabled = true;
      removeDiscount(removeCode.dataset.illRemoveCode);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.closest('#mini-cart')) return;

    if (form.matches('[data-ill-discount-form]')) {
      event.preventDefault();
      const code = (form.elements.discount?.value || '').trim();
      if (!code) {
        discountMessage = { type: 'error', text: 'Bitte gib einen Aktionscode ein.' };
        paintDiscountMessage();
        return;
      }
      applyDiscount(code);
      return;
    }

    // Checkout: erst offene Mengenänderungen und den MBC-Bundle-Rabatt abschließen
    const bundleSyncing = getDrawer()?.classList.contains('is-syncing');
    if (form.matches('[data-ill-checkout-form]') && (pending.size > 0 || queueLength > 0 || bundleSyncing)) {
      event.preventDefault();
      const button = getDrawer()?.querySelector('.ill-cart__checkout');
      button?.setAttribute('aria-busy', 'true');
      flush().then(waitForBundleSync).then(() => form.submit());
    }
  });

  /* ------------------------------------------------------------------
   * Upsells: KI-Empfehlungen (Shopify Recommendations API) oder manuelle Produkte,
   * als Karussell mit Pfeilen und Punkten. Produkte, die schon im Warenkorb sind,
   * werden ausgeblendet (außer „Auch anzeigen …“ ist aktiv).
   * ------------------------------------------------------------------ */
  const MAX_UPSELL_SOURCES = 6; // für wie viele Warenkorb-Produkte Empfehlungen geladen werden
  // WICHTIG: Shopify lässt in Empfehlungen alles weg, was GERADE im Warenkorb liegt.
  // Deshalb gilt jede geladene Liste nur für genau diesen Warenkorb-Stand (cartSig) und
  // wird nach jeder Änderung neu geladen – sonst kämen entfernte Produkte nie zurück.
  const recommendationHtml = new Map(); // "productId|intent|cartSig" -> HTML
  const recommendationRequests = new Map(); // "productId|intent|cartSig" -> laufender Request
  let upsellIndex = 0;
  let lastSlides = new Map(); // productId -> Slide vom letzten Rendern (Wiederverwendung, kein Flackern)

  function loadRecommendations(productId, intent, cartSig) {
    const key = `${productId}|${intent}|${cartSig}`;
    if (!recommendationRequests.has(key)) {
      const base = window.themeVariables?.routes?.productRecommendationsUrl || `${shopRoot()}recommendations/products`;
      // ill_cart: eigene URL pro Warenkorb-Stand, damit der Browser keine alte Antwort aus dem Cache nimmt
      const url = `${base}?section_id=${encodeURIComponent(getSectionId())}&product_id=${encodeURIComponent(productId)}&limit=10&intent=${encodeURIComponent(intent)}&ill_cart=${encodeURIComponent(cartSig)}`;
      recommendationRequests.set(key, nativeFetch(url, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          recommendationHtml.set(key, doc.querySelector('[data-ill-upsell-results]')?.innerHTML || '');
        })
        .catch(() => recommendationHtml.set(key, ''))); // Fehler: leer merken, kein erneuter Versuch in Schleife
    }
    return recommendationRequests.get(key);
  }

  /* Zuletzt entfernte Produkte merken (pro Browser-Sitzung) – sie werden als Upsell zuerst angezeigt. */
  const REMOVED_KEY = 'ill-cart-removed-products';
  let previousCartIds = null;
  let recentlyRemoved = (() => {
    try { return JSON.parse(sessionStorage.getItem(REMOVED_KEY)) || []; } catch (e) { return []; }
  })();

  function trackRemovedProducts() {
    const current = new Set(Array.from(getDrawer()?.querySelectorAll('[data-ill-item]') || []).map((item) => item.dataset.productId));
    if (previousCartIds) {
      const removed = [...previousCartIds].filter((id) => !current.has(id));
      recentlyRemoved = [...removed, ...recentlyRemoved.filter((id) => !removed.includes(id))];
    }
    recentlyRemoved = recentlyRemoved.filter((id) => !current.has(id)).slice(0, 10);
    previousCartIds = current;
    try { sessionStorage.setItem(REMOVED_KEY, JSON.stringify(recentlyRemoved)); } catch (e) { /* Privat-Modus */ }
  }

  // Empfehlungen ALLER Warenkorb-Produkte zusammenführen (ohne Duplikate),
  // zuletzt entfernte Produkte zuerst.
  function fillAiTrack(container, ids, intent, cartSig) {
    const track = container.querySelector('[data-ill-upsell-track]');
    const keys = ids.map((id) => `${id}|${intent}|${cartSig}`).filter((key) => recommendationHtml.has(key));

    // Neuer Warenkorb-Stand noch nicht geladen: bisherige Upsells weiter zeigen (kein Leerlauf/Flackern)
    if (!keys.length) {
      if (lastSlides.size && !track.children.length) track.replaceChildren(...lastSlides.values());
      return buildUpsells(container);
    }

    const fillKey = `${keys.join(',')}|${recentlyRemoved.join(',')}`;
    if (track.dataset.illFilled === fillKey) return buildUpsells(container);

    const template = document.createElement('template');
    const seen = new Set();
    let slides = [];
    keys.forEach((key) => {
      template.innerHTML = recommendationHtml.get(key);
      template.content.querySelectorAll('[data-ill-upsell]').forEach((slide) => {
        const productId = slide.dataset.productId;
        if (seen.has(productId)) return;
        seen.add(productId);
        slides.push(lastSlides.get(productId) || slide);
      });
    });

    const rank = (slide) => {
      const index = recentlyRemoved.indexOf(slide.dataset.productId);
      return index === -1 ? recentlyRemoved.length : index;
    };
    slides = slides.map((slide, order) => ({ slide, order }))
      .sort((a, b) => rank(a.slide) - rank(b.slide) || a.order - b.order)
      .map((entry) => entry.slide);

    track.replaceChildren(...slides);
    track.dataset.illFilled = fillKey;
    lastSlides = new Map(slides.map((slide) => [slide.dataset.productId, slide]));
    return buildUpsells(container);
  }

  function setupUpsells() {
    const container = getDrawer()?.querySelector('[data-ill-upsells]');
    if (!container) return;

    if (container.dataset.source !== 'ai') {
      buildUpsells(container);
      return;
    }

    const allIds = (container.dataset.productIds || '').split(',').filter(Boolean);
    const ids = [...allIds].reverse().slice(0, MAX_UPSELL_SOURCES);
    if (!ids.length) return;

    const intent = container.dataset.intent;
    const cartSig = [...allIds].sort().join('-');
    fillAiTrack(container, ids, intent, cartSig);

    const missing = ids.filter((id) => !recommendationRequests.has(`${id}|${intent}|${cartSig}`));
    if (!missing.length) return;

    // Sofort laden, wenn der Warenkorb offen ist – sonst, wenn der Browser Zeit hat
    const load = () => Promise.all(missing.map((id) => loadRecommendations(id, intent, cartSig))).then(() => {
      if (container.isConnected) setupUpsells();
    });
    if (getDrawer()?.hasAttribute('open')) load();
    else (window.requestIdleCallback || ((cb) => setTimeout(cb, 2000)))(load);
  }

  function buildUpsells(container) {
    const track = container.querySelector('[data-ill-upsell-track]');
    const inCart = new Set(Array.from(getDrawer()?.querySelectorAll('[data-ill-item]') || []).map((item) => item.dataset.productId));
    const showInCart = container.dataset.showInCart === 'true';
    const limit = parseInt(container.dataset.limit, 10) || 10;

    let shown = 0;
    track.querySelectorAll('[data-ill-upsell]').forEach((slide) => {
      const visible = shown < limit && (showInCart || !inCart.has(slide.dataset.productId));
      slide.hidden = !visible;
      if (visible) shown++;
    });

    container.hidden = shown === 0;
    if (!shown) return 0;

    const dots = container.querySelector('[data-ill-upsell-dots]');
    dots.replaceChildren(...Array.from({ length: shown }, (_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ill-upsells__dot';
      dot.dataset.illUpsellDot = i;
      dot.setAttribute('aria-label', `Produkt ${i + 1} von ${shown}`);
      return dot;
    }));
    dots.hidden = shown < 2;
    container.querySelectorAll('[data-ill-upsell-nav]').forEach((arrow) => { arrow.hidden = shown < 2; });

    upsellIndex = Math.min(upsellIndex, shown - 1);
    track.scrollTo({ left: upsellIndex * track.clientWidth, behavior: 'instant' });
    if (!track.dataset.illBound) {
      track.dataset.illBound = 'true';
      track.addEventListener('scroll', () => {
        if (track.clientWidth) upsellIndex = Math.round(track.scrollLeft / track.clientWidth);
        paintUpsellNav(container, container.querySelectorAll('[data-ill-upsell-dot]').length);
      }, { passive: true });
    }
    paintUpsellNav(container, shown);
    return shown;
  }

  function paintUpsellNav(container, count) {
    container.querySelectorAll('[data-ill-upsell-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === upsellIndex));
    const [prev, next] = container.querySelectorAll('[data-ill-upsell-nav]');
    if (prev) prev.disabled = upsellIndex <= 0;
    if (next) next.disabled = upsellIndex >= count - 1;
  }

  function goToUpsell(container, index) {
    const track = container.querySelector('[data-ill-upsell-track]');
    upsellIndex = Math.max(0, index);
    track.scrollTo({ left: upsellIndex * track.clientWidth, behavior: 'smooth' });
  }

  // Artikel hinzufügen (Upsell oder Variantenauswahl)
  function addVariant(variantId, button) {
    button?.setAttribute('aria-busy', 'true');
    return flush()
      .then(() => enqueue(async () => {
        const data = await cartRequest('add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
        if (data.sections && data.sections[getSectionId()]) {
          root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: { sections: data.sections }, replacementDelay: 0 } }));
        } else {
          refreshFromServer();
        }
        const cart = await fetchCart();
        root.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      }))
      .finally(() => button?.removeAttribute('aria-busy'));
  }

  /* Variantenauswahl (Bottom Sheet) für Upsells mit mehreren Varianten */
  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function openPicker(data) {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.querySelector('.ill-picker')?.remove();

    const initial = data.variants.find((v) => v.id === data.selected) || data.variants.find((v) => v.available) || data.variants[0];
    const selected = [...initial.options];

    const picker = el('div', 'ill-picker');
    picker.setAttribute('role', 'dialog');
    picker.setAttribute('aria-modal', 'true');
    picker.setAttribute('aria-label', `${data.title}: Variante wählen`);

    const backdrop = el('div', 'ill-picker__backdrop');
    backdrop.dataset.illPickerClose = '';
    const sheet = el('div', 'ill-picker__sheet');

    const close = el('button', 'ill-picker__close');
    close.type = 'button';
    close.dataset.illPickerClose = '';
    close.setAttribute('aria-label', 'Schließen');
    close.innerHTML = '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 16 16"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.8" fill="none"/></svg>';

    const head = el('div', 'ill-picker__head');
    const image = el('img', 'ill-picker__image');
    image.alt = '';
    const headText = el('div');
    const priceRow = el('p', 'ill-picker__price');
    const amount = el('span', 'ill-picker__amount');
    const compare = el('s', 'ill-picker__compare');
    priceRow.append(amount, compare);
    headText.append(el('p', 'ill-picker__title', data.title), priceRow);
    head.append(image, headText);

    const optionsWrap = el('div');
    const submit = el('button', 'ill-picker__submit', 'In den Warenkorb');
    submit.type = 'button';
    const error = el('p', 'ill-picker__error');
    error.hidden = true;

    const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    function paint() {
      const variant = findVariant(selected);
      image.src = (variant && variant.image) || data.image || '';
      image.hidden = !image.src;
      // Preise kommen als HTML aus Liquid ({{ price | money }}), z. B. von Transcy in
      // <span class="transcy-money"> verpackt -> als HTML einsetzen, nicht als Text
      amount.innerHTML = variant ? variant.price : '';
      compare.innerHTML = variant && variant.compare ? variant.compare : '';
      compare.hidden = !compare.textContent;
      submit.disabled = !variant || !variant.available;
      submit.textContent = variant && !variant.available ? 'Ausverkauft' : 'In den Warenkorb';

      optionsWrap.replaceChildren(...data.options.map((name, index) => {
        const group = el('div', 'ill-picker__option');
        const values = el('div', 'ill-picker__values');
        const unique = [...new Set(data.variants.map((v) => v.options[index]))];
        values.append(...unique.map((value) => {
          const button = el('button', 'ill-picker__value', value);
          button.type = 'button';
          const candidate = [...selected];
          candidate[index] = value;
          const match = findVariant(candidate);
          button.classList.toggle('is-selected', selected[index] === value);
          button.classList.toggle('is-unavailable', !match || !match.available);
          button.setAttribute('aria-pressed', String(selected[index] === value));
          button.addEventListener('click', () => {
            selected[index] = value;
            error.hidden = true;
            paint();
          });
          return button;
        }));
        group.append(el('p', 'ill-picker__label', name), values);
        return group;
      }));
    }

    submit.addEventListener('click', () => {
      const variant = findVariant(selected);
      if (!variant || !variant.available) return;
      addVariant(variant.id, submit)
        .then(() => picker.remove())
        .catch((err) => {
          error.textContent = err.message;
          error.hidden = false;
        });
    });

    picker.addEventListener('click', (event) => {
      if (event.target.closest('[data-ill-picker-close]')) picker.remove();
    });

    sheet.append(close, head, optionsWrap, submit, error);
    picker.append(backdrop, sheet);
    paint();
    drawer.append(picker);
    submit.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------
   * MBC Bundles: Drawer sofort öffnen
   * MBC meldet sich erst nach mehreren Schritten (Gratis-Geschenke, Attribute,
   * Bundle-Rabatt über den MBC-Server …) – das dauert 3–4 s. Deshalb öffnen wir,
   * sobald MBCs „Hinzufügen“-Request (…/cart/add.js?mbcb-request) erfolgreich war.
   * Klickt jemand auf „Zur Kasse“, bevor MBC den Bundle-Rabatt angewendet hat,
   * wartet der Checkout kurz, bis MBC fertig ist (sonst fehlt der Rabatt).
   * ------------------------------------------------------------------ */
  const BUNDLE_SYNC_TIMEOUT = 10000;
  let bundleSyncTimer = null;
  let bundleSyncFinished = false;

  let bundleSyncWaiters = [];

  function setBundleSyncing(active) {
    clearTimeout(bundleSyncTimer);
    bundleSyncFinished = false;
    getDrawer()?.classList.toggle('is-syncing', active);
    if (active) {
      bundleSyncTimer = setTimeout(() => setBundleSyncing(false), BUNDLE_SYNC_TIMEOUT);
    } else {
      bundleSyncWaiters.forEach((resolve) => resolve());
      bundleSyncWaiters = [];
    }
  }

  const waitForBundleSync = () => (getDrawer()?.classList.contains('is-syncing')
    ? new Promise((resolve) => bundleSyncWaiters.push(resolve))
    : Promise.resolve());

  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const request = nativeFetch.apply(this, args);
    const url = String(typeof args[0] === 'string' ? args[0] : args[0]?.url || '');

    if (url.includes('/cart/add') && url.includes('mbcb-request')) {
      request.then((response) => {
        if (!response.ok) return;
        setBundleSyncing(true);
        root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { openMiniCart: true } }));
      }).catch(() => {});
    }
    return request;
  };

  // MBC ist fertig: das nächste Neurendern zeigt die finalen Preise
  document.addEventListener('MAKEBECOOL_BUNDLES.updateCart', () => {
    if (getDrawer()?.classList.contains('is-syncing')) bundleSyncFinished = true;
  });

  /* ------------------------------------------------------------------
   * Smartphone-Tastatur (v. a. iPhone): Safari verkleinert beim Öffnen der Tastatur
   * nicht den Bereich, an dem sich der Drawer ausrichtet – die Tastatur legt sich
   * über das Aktionscode-Feld. Solange die Tastatur offen ist, richten wir den Drawer
   * deshalb am tatsächlich sichtbaren Bereich aus (VisualViewport-API).
   * ------------------------------------------------------------------ */
  const visualViewport = window.visualViewport;
  let viewportFrame = null;

  function fitDrawerToKeyboard() {
    viewportFrame = null;
    const drawer = getDrawer();
    if (!drawer || !visualViewport) return;

    const keyboardOpen = drawer.hasAttribute('open')
      && drawer.contains(document.activeElement)
      && document.activeElement.matches('input, textarea')
      && window.innerHeight - visualViewport.height > 120;

    drawer.classList.toggle('is-keyboard-open', keyboardOpen);
    drawer.style.height = keyboardOpen ? `${visualViewport.height}px` : '';
    drawer.style.top = keyboardOpen ? `${visualViewport.offsetTop}px` : '';
  }

  function scheduleFit() {
    if (!viewportFrame) viewportFrame = setTimeout(fitDrawerToKeyboard, 0);
  }

  if (visualViewport) {
    visualViewport.addEventListener('resize', scheduleFit);
    visualViewport.addEventListener('scroll', scheduleFit);
    document.addEventListener('focusin', scheduleFit);
    document.addEventListener('focusout', () => setTimeout(scheduleFit, 50));
  }

  // Theme-Editor: Section wird bei Änderungen komplett ersetzt
  document.addEventListener('shopify:section:load', observeDrawer);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeDrawer);
  } else {
    observeDrawer();
  }
})();
